import { create } from 'zustand';

import {
  ApiChatPermissionResponse,
  ApiPermissionResponse,
  ApiRoleResponse,
  ApiUserPermissionResponse,
  ApiUserRoleResponse,
} from '@/shared/api/generated/data-contracts';

type PermissionsStoreState = {
  userRoles: ApiUserRoleResponse[];
  userPermissions: ApiUserPermissionResponse[];
  allRoles: ApiRoleResponse[];
  allPermissions: ApiPermissionResponse[];
  chatPermissions: ApiChatPermissionResponse | null;
  isLoading: boolean;
  isInitialized: boolean;
};

type PermissionsStore = PermissionsStoreState & {
  setUserRoles: (roles: ApiUserRoleResponse[]) => void;
  setUserPermissions: (permissions: ApiUserPermissionResponse[]) => void;
  setAllRoles: (roles: ApiRoleResponse[]) => void;
  setAllPermissions: (permissions: ApiPermissionResponse[]) => void;
  setChatPermissions: (chatPermissions: ApiChatPermissionResponse | null) => void;
  addUserRole: (role: ApiUserRoleResponse) => void;
  removeUserRole: (roleId: string) => void;
  addUserPermission: (permission: ApiUserPermissionResponse) => void;
  removeUserPermission: (permissionId: string) => void;
  setLoading: (isLoading: boolean) => void;
  setInitialized: (isInitialized: boolean) => void;
};

export const usePermissionsStore = create<PermissionsStore>((set) => ({
  userRoles: [],
  userPermissions: [],
  allRoles: [],
  allPermissions: [],
  chatPermissions: null,
  isLoading: false,
  isInitialized: false,

  setUserRoles: (roles) => set({ userRoles: roles }),
  setUserPermissions: (permissions) => set({ userPermissions: permissions }),
  setAllRoles: (roles) => set({ allRoles: roles }),
  setAllPermissions: (permissions) => set({ allPermissions: permissions }),
  setChatPermissions: (chatPermissions) => set({ chatPermissions }),

  addUserRole: (newRole) =>
    set((state) => {
      const existingIndex = state.userRoles.findIndex((r) => r.id === newRole.id);
      if (existingIndex !== -1) {
        const updatedRoles = [...state.userRoles];
        updatedRoles[existingIndex] = newRole;
        return { userRoles: updatedRoles };
      }
      return { userRoles: [newRole, ...state.userRoles] };
    }),

  removeUserRole: (roleId) =>
    set((state) => ({
      userRoles: state.userRoles.filter((r) => r.id !== roleId),
    })),

  addUserPermission: (newPermission) =>
    set((state) => {
      const existingIndex = state.userPermissions.findIndex((p) => p.id === newPermission.id);
      if (existingIndex !== -1) {
        const updatedPermissions = [...state.userPermissions];
        updatedPermissions[existingIndex] = newPermission;
        return { userPermissions: updatedPermissions };
      }
      return { userPermissions: [newPermission, ...state.userPermissions] };
    }),

  removeUserPermission: (permissionId) =>
    set((state) => ({
      userPermissions: state.userPermissions.filter((p) => p.id !== permissionId),
    })),

  setLoading: (isLoading) => set({ isLoading }),
  setInitialized: (isInitialized) => set({ isInitialized }),
}));
